//@ts-check
import React, { memo, useContext, useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { ActionMenu } from '../../../../../../Components/ActionMenu'
import { useGetActionsCard } from './ActionMenu'
import {
  CAMPAIGN_ENUM_TYPES,
  CAMPAIGN_SUBTYPES_ENUM,
} from '../../../../../../shared/constants'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import Box from '@material-ui/core/Box'
import { SnackBarContext } from '../../../../../../Components/SnackBar'
import Tooltip from '@material-ui/core/Tooltip'
import Paper from '@material-ui/core/Paper'
import { theme } from '../../../../../../Assets/Styles/theme'
import { formatCurrency, getDashboardPageUrl } from '../utils'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import VisibilityIcon from '@material-ui/icons/Visibility';
import Link from '@material-ui/core/Link'
import DesignPreviewModal from '../../../../../DesignManagement/DesignPreviewModal/DesignPreviewModal'
import { IconButton } from '@material-ui/core'
import moment from 'moment'
/** @import { Campaign, FlyerDesign } from '../utils' */

/**
 * @param {{
 *  item: Campaign,
 *  currencyName: string,
 *  clearHeight: () => void,
 * }} props
 */
function CampaignCard_({ item, currencyName, clearHeight }) {
  const {
    campaignId,
    campaignName,
    internalStatusChangedDate,
    clientName,
    clientId,
    totalCosts,
    startDate,
    flyerType,
    flyerCount,
    flyerDesign,
    type,
    subtype,
    cityName,
    flyerWeight,
    printerJobs,
  } = item

  const mailKinds = {
    [CAMPAIGN_SUBTYPES_ENUM.EDDM]: 'EDDM',
    [CAMPAIGN_SUBTYPES_ENUM.ADM]: 'ADM',
  }

  const flyerKinds = {
    [CAMPAIGN_ENUM_TYPES.HANDTOHAND]: 'H2H',
    [CAMPAIGN_ENUM_TYPES.LETTERBOX]: 'D2D',
  }

  const classes = useStyles()
  const options = useGetActionsCard(item)
  const typeLabels = { ...mailKinds, ...flyerKinds }
  const joinedType = subtype || type
  const isFlyer = !!flyerKinds[joinedType]
  const isMail = !!mailKinds[joinedType]
  const numberFmt = new Intl.NumberFormat()
  const flyerTypeFmt = flyerType.startsWith("POSTCARD") ? flyerType.split("_") : [flyerType]
  const flyerWeightFmt = isFlyer ? [`${flyerWeight || '-'} GSM`] : []
  const printerJobsFmt = printerJobs ? printerJobs.edges.map(({ node }) => ({ label: node.printerName, value: node.mailPieces })) : undefined
  const mailerCount = printerJobs ? printerJobs.edges.reduce((x, y) => x + y.node.mailPieces, 0) : undefined

  return (
    <Card variant="outlined">
      <CardContent
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          padding: 12,
        }}
      >
        <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <CopyCB>{campaignId}</CopyCB>
          <Typography className={classes.subTitle3}>
            Last update: {moment(Number(internalStatusChangedDate)).format("ll")}
          </Typography>
          <ActionMenu options={options} />
        </Box>

        <Box style={{ display: "flex", justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
          <Box style={{ overflow: "hidden" }}>
            <Link href={`/dashboard/clients/${clientId}`}>
              <Ellipsis className={classes.subTitle2} >
                {clientName}
              </Ellipsis>
            </Link>
            <Link href={getDashboardPageUrl(campaignId, window.location.origin, type, subtype)}>
              <Ellipsis className={classes.subTitle1} style={{ paddingTop: 3 }}>
                {campaignName}
              </Ellipsis>
            </Link>
          </Box>
          {flyerDesign && <DesignPreviewBtn source={flyerDesign} />}
        </Box>
        <Row label="Launch Date" value={moment(Number(startDate)).format("ll")} />
        <Row label="Deal Value" value={formatCurrency(totalCosts, currencyName)} />
        {isFlyer && <Row label="Flyers" value={flyerCount ? numberFmt.format(flyerCount) : '-'} />}
        {isFlyer && <Row label="City" value={cityName || '-'} />}
        {isMail && mailerCount && <Row label="Mailers" value={mailerCount ? numberFmt.format(mailerCount) : '-'} />}
        {isMail && printerJobsFmt && <VendorAccordion clearHeight={clearHeight} campaignId={campaignId} data={printerJobsFmt} />}
        <Chip label={typeLabels[joinedType] || '-'} values={[...flyerTypeFmt, ...flyerWeightFmt]} />
      </CardContent>
    </Card>
  )
}

/**
 * @param {{ source: FlyerDesign }} props
 */
function DesignPreviewBtn(props) {
  const [open, setOpen] = useState(false)

  return (
    <Box>
      <Tooltip title="Preview Artwork">
        <IconButton onClick={() => setOpen(true)}>
          <VisibilityIcon style={{ color: theme.colors.deepBlue }} />
        </IconButton>
      </Tooltip>
      <DesignPreviewModal
        isOpen={open}
        onClose={() => setOpen(false)}
        designData={props.source}
        designName={props.source.fileName}
      />
    </Box>
  )
}

/**
 * @param {{
 *   data: { label: string, value: number, }[],
 *   clearHeight: () => void,
 *   campaignId: string,
 * }} props
 */
function VendorAccordion({ data, clearHeight, campaignId }) {
  const classes = useStyles()
  const [expanded, setExpanded] = useState(false);
  const numberFmt = new Intl.NumberFormat()
  function toggle() {
    setExpanded(!expanded)
    clearHeight()
  }

  return (
    <Box style={{
      border: expanded ? 'solid 1px #AEAFC7' : 'none',
      borderRadius: 4,
      backgroundColor: expanded ? 'unset' : '#F9F9FF',
      padding: 6,
    }}>
      <Box style={{ display: "flex", alignItems: "center" }} onClick={toggle}>
        <Typography className={classes.subTitle3} style={{ marginRight: "auto" }}>Printer Vendors:</Typography>
        <Typography className={classes.subTitle3} style={{ color: theme.colors.deepBlue }}>{data.length}</Typography>
        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Box>
      {expanded &&
        <Box style={{ overflowY: "auto", maxHeight: 200, paddingTop: 8 }}>
          <Box>
            {data.map(({ label, value }) =>
              <Link href={`/dashboard/campaigns/${campaignId}/internal-files`}>
                <Row key={label} label={label} value={numberFmt.format(value)} />
              </Link>
            )}
          </Box>
        </Box>
      }
    </Box>
  )

}

/**
 * @param {{ children: string }} props
 */
function CopyCB({ children }) {
  const runSnackBar = useContext(SnackBarContext)
  const classes = useStyles()

  return (
    <Tooltip title="copy to clipboard">
      <Box
        onClick={() =>
          navigator.clipboard
            .writeText(children)
            .then(() =>
              runSnackBar({
                type: 'info',
                msg: 'copied to clipboard',
              }),
            )
            .catch(() =>
              runSnackBar({
                type: 'error',
                msg: 'clipboard not available',
              }),
            )
        }
      >
        <Button className={`${classes.cpBtn} ${classes.subTitle3}`}>{children}</Button>
      </Box>
    </Tooltip>
  )
}

/**
 * @param {{ label: string, value: string }} props
 */
function Row({ label, value }) {
  const classes = useStyles()
  return (
    <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
      <Ellipsis
        className={classes.subTitle3}
        style={{ whiteSpace: 'pre' }}
      >
        {`${label}: `}
      </Ellipsis>
      <Ellipsis className={classes.subTitle3} style={{ color: theme.colors.deepBlue }}>{value}</Ellipsis>
    </Box>
  )
}

/**
 * @param {{ label: string, values: string[] }} props
 */
function Chip({ label, values }) {
  const classes = useStyles()
  const [first, second, ...others] = values

  return (
    <Paper elevation={0} square className={classes.surface}>
      <Box className={classes.row}>
        <Typography className={classes.label}>{label}</Typography>
        {first && <Typography className={classes.values}>{first}</Typography>}
        {second && <Typography className={classes.values}>{second}</Typography>}
      </Box>
      <Box className={classes.row}>
        {others.map(v => (
          <Typography key={v} className={classes.values}>
            {v}
          </Typography>
        ))}
      </Box>
    </Paper>
  )
}

/**
 * @param {Parameters<typeof Typography>[0] & { children: string }} props
 */
function Ellipsis({ style, children, ...props }) {
  const [showTooltip, setShowTooltip] = useState(false)
  return (
    <Tooltip title={showTooltip ? children : ""}>
      <Typography
        {...props}
        onMouseEnter={({ currentTarget: { scrollWidth, clientWidth } }) => {
          setShowTooltip(scrollWidth > clientWidth)
        }}
        style={{
          ...style,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
          whiteSpace: 'nowrap',
        }}
      >
        {children}
      </Typography>
    </Tooltip>
  )
}

const useStyles = makeStyles({
  surface: {
    marginTop: 3,
    padding: 6,
    backgroundColor: '#F9F9FF',
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  label: {
    color: '#4F5B94',
    fontSize: '.76rem',
    fontWeight: 600,
  },
  values: {
    color: '#4F5B94',
    fontSize: '.76rem',
    fontWeight: 400,
  },
  cpBtn: {
    minWidth: 0,
    padding: 0,
    '&:hover': {
      backgroundColor: 'unset',
    },
  },
  subTitle1: {
    fontSize: 14,
    fontWeight: 700,
    lineHeight: '18px',
    color: theme.colors.deepPink,
  },
  subTitle2: {
    fontSize: 14,
    fontWeight: 600,
    lineHeight: '20px',
    color: theme.colors.deepBlue,
  },
  subTitle3: {
    fontSize: 12,
    fontWeight: 400,
    lineHeight: '15px',
    color: theme.colors.silver3,
  },
})

export const CampaignCard = memo(CampaignCard_)
